/**
 * Audit Log API - Direct Supabase REST API calls
 * 
 * Provides functions to query audit logs for Super Admin and Admin users.
 * Tracks all create, update, and delete operations on critical tables.
 * 
 * Requirements: 2.4.6, 2.5.5, 4.2.2
 */

import type { AuditLogEntry, AuditLogFilters } from '@timetable/types'
import { getAccessToken } from './auth-token'

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string


/**
 * List audit log entries with optional filters
 * 
 * Super Admin can see all logs across all schools.
 * Admin can see logs for their school only.
 * 
 * @param filters - Optional filters for querying audit logs
 * @returns Promise<AuditLogEntry[]>
 */
export async function listAuditLogsDirect(
  filters?: AuditLogFilters
): Promise<AuditLogEntry[]> {
  const accessToken = getAccessToken()
  if (!accessToken) throw new Error('Not authenticated')

  const params = new URLSearchParams()
  params.append('select', '*')
  
  // Apply filters
  if (filters?.userId) {
    params.append('user_id', `eq.${filters.userId}`)
  }
  if (filters?.schoolContext) {
    params.append('school_context', `eq.${filters.schoolContext}`)
  }
  if (filters?.tableName) {
    params.append('table_name', `eq.${filters.tableName}`)
  }
  if (filters?.operation) {
    params.append('operation', `eq.${filters.operation}`)
  }
  if (filters?.startDate) {
    params.append('timestamp', `gte.${filters.startDate}`)
  }
  if (filters?.endDate) {
    params.append('timestamp', `lte.${filters.endDate}`)
  }
  
  // Pagination
  params.append('limit', String(filters?.limit || 100))
  if (filters?.offset) {
    params.append('offset', String(filters.offset))
  }
  
  // Order by timestamp descending (most recent first)
  params.append('order', 'timestamp.desc')

  const response = await fetch(`${SUPABASE_URL}/rest/v1/audit_log?${params}`, {
    headers: {
      'apikey': SUPABASE_ANON_KEY,
      'Authorization': `Bearer ${accessToken}`,
    },
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.message || 'Failed to fetch audit logs')
  }

  return await response.json()
}

/**
 * Get audit log entry by ID
 * 
 * @param id - Audit log entry ID
 * @returns Promise<AuditLogEntry>
 */
export async function getAuditLogDirect(id: string): Promise<AuditLogEntry> {
  const accessToken = getAccessToken()
  if (!accessToken) throw new Error('Not authenticated')

  const params = new URLSearchParams()
  params.append('select', '*')
  params.append('id', `eq.${id}`)

  const response = await fetch(`${SUPABASE_URL}/rest/v1/audit_log?${params}`, {
    headers: {
      'apikey': SUPABASE_ANON_KEY,
      'Authorization': `Bearer ${accessToken}`,
      'Accept': 'application/vnd.pgrst.object+json',
    },
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.message || 'Audit log entry not found')
  }

  return await response.json()
}

/**
 * Get audit logs for a specific record
 * 
 * Returns all audit log entries for a specific record across all operations.
 * Useful for viewing the complete history of changes to a record.
 * 
 * @param tableName - Name of the table
 * @param recordId - ID of the record
 * @returns Promise<AuditLogEntry[]>
 */
export async function getRecordAuditHistoryDirect(
  tableName: string,
  recordId: string
): Promise<AuditLogEntry[]> {
  const accessToken = getAccessToken()
  if (!accessToken) throw new Error('Not authenticated')

  const params = new URLSearchParams()
  params.append('select', '*')
  params.append('table_name', `eq.${tableName}`)
  params.append('record_id', `eq.${recordId}`)
  params.append('order', 'timestamp.desc')

  const response = await fetch(`${SUPABASE_URL}/rest/v1/audit_log?${params}`, {
    headers: {
      'apikey': SUPABASE_ANON_KEY,
      'Authorization': `Bearer ${accessToken}`,
    },
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.message || 'Failed to fetch audit history')
  }

  return await response.json()
}

/**
 * Get audit log statistics
 * 
 * Returns summary statistics about audit log entries.
 * Super Admin only.
 * 
 * @param filters - Optional filters for statistics
 * @returns Promise with statistics
 */
export async function getAuditLogStatsDirect(filters?: {
  schoolContext?: string
  startDate?: string
  endDate?: string
}): Promise<{
  totalOperations: number
  operationsByType: Record<string, number>
  operationsByTable: Record<string, number>
  operationsByUser: Record<string, number>
}> {
  const accessToken = getAccessToken()
  if (!accessToken) throw new Error('Not authenticated')

  // Fetch all audit logs with filters
  const logs = await listAuditLogsDirect({
    schoolContext: filters?.schoolContext,
    startDate: filters?.startDate,
    endDate: filters?.endDate,
    limit: 10000, // Get all for statistics
  })

  // Calculate statistics
  const operationsByType: Record<string, number> = {}
  const operationsByTable: Record<string, number> = {}
  const operationsByUser: Record<string, number> = {}

  logs.forEach((log) => {
    // Count by operation type
    operationsByType[log.operation] = (operationsByType[log.operation] || 0) + 1

    // Count by table
    operationsByTable[log.table_name] = (operationsByTable[log.table_name] || 0) + 1

    // Count by user
    if (log.user_id) {
      operationsByUser[log.user_id] = (operationsByUser[log.user_id] || 0) + 1
    }
  })

  return {
    totalOperations: logs.length,
    operationsByType,
    operationsByTable,
    operationsByUser,
  }
}

/**
 * Get recent Super Admin actions
 * 
 * Returns recent audit log entries for Super Admin operations only.
 * Filters for operations on schools and users tables.
 * 
 * @param limit - Number of entries to return (default: 50)
 * @returns Promise<AuditLogEntry[]>
 */
export async function getRecentSuperAdminActionsDirect(
  limit: number = 50
): Promise<AuditLogEntry[]> {
  const accessToken = getAccessToken()
  if (!accessToken) throw new Error('Not authenticated')

  const params = new URLSearchParams()
  params.append('select', '*')
  params.append('or', '(table_name.eq.schools,table_name.eq.users)')
  params.append('order', 'timestamp.desc')
  params.append('limit', String(limit))

  const response = await fetch(`${SUPABASE_URL}/rest/v1/audit_log?${params}`, {
    headers: {
      'apikey': SUPABASE_ANON_KEY,
      'Authorization': `Bearer ${accessToken}`,
    },
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.message || 'Failed to fetch Super Admin actions')
  }

  return await response.json()
}
